import { createSelector } from '@reduxjs/toolkit';
import { groupBy } from 'lodash';
import {
  selectWellDetailsSchematic,
  selectWellDetailsWell,
  selectIsGenerateFromSchematic,
} from 'store/entities/well-details/selectors';
import { selectSectionsDataSections } from 'store/entities/sections-data-structure/selector';
import { linkFields as linkFieldsEnum, operationCodesTypes } from 'src/enums/settings';
import { casingTypes, casingStringType } from 'src/enums/well-details';
import { split, withUnit } from '@oliasoft-open-source/units';
import { Sections } from 'src/enums/sections';

const selectActivityModel = ({ entities }) => entities.activityModel;

export const selectActivityModelLoaders = createSelector(
  [selectActivityModel],
  ({ isAdding, isFetching, isDeleting, isReordering }) => ({
    isAdding,
    isFetching,
    isDeleting,
    isReordering,
  }),
);

export const selectImportExcelSuccess = createSelector(
  [selectActivityModel],
  (activityModel) => activityModel.importExcelSuccess,
);

export const selectOperationList = createSelector([selectActivityModel], (activityModel) => activityModel.operations);

export const selectSectionsList = createSelector([selectActivityModel], (activityModel) =>
  activityModel.sections
    .filter((section) => activityModel.operations.find((operation) => operation.sectionId === section.sectionId))
    .sort((a, b) => a.name.localeCompare(b.name)),
);

export const selectGroupedOperationList = createSelector([selectOperationList], (operations) =>
  Object.values(groupBy(operations, 'operationHeader'))?.sort(
    (a, b) => a[0].operationHeaderPosition - b[0].operationHeaderPosition,
  ),
);

export const selectActiveOperation = createSelector([selectOperationList], (operations) =>
  operations.find((item) => item.active),
);

export const selectLinkFields = createSelector([selectActivityModel], (activityModel) => activityModel.linkFields);

const addDefaultSchematics = (schematic, wellDetails) => {
  const copiedSchematics = schematic.slice();
  const createDefaultSchematic = (type, name) => {
    return { type, name };
  };

  const defaults = [
    ...(wellDetails.mobilization ? [createDefaultSchematic(casingStringType.MOBILIZATION, casingTypes.NONAME)] : []),
    ...copiedSchematics,
    ...(wellDetails.lowerCompletion ? [createDefaultSchematic(casingStringType.COMPLETION, casingTypes.LOWER)] : []),
    ...(wellDetails.middleCompletion ? [createDefaultSchematic(casingStringType.COMPLETION, casingTypes.MIDDLE)] : []),
    ...(wellDetails.upperCompletion ? [createDefaultSchematic(casingStringType.COMPLETION, casingTypes.UPPER)] : []),
    ...(wellDetails.xmt ? [createDefaultSchematic(casingStringType.XMT, casingTypes.NONAME)] : []),
    ...(wellDetails.demobilization
      ? [createDefaultSchematic(casingStringType.DEMOBILIZATION, casingTypes.NONAME)]
      : []),
  ];

  return defaults;
};

const mapAndSortGroups = (groups) =>
  groups
    .map((group, index) =>
      group.map((operation) => ({
        ...operation,
        operationHeaderPosition: index + 1,
      })),
    )
    .sort((a, b) => a[0].operationHeaderPosition - b[0].operationHeaderPosition);

export const selectGroupedOperationListWithDefaults = createSelector(
  [selectGroupedOperationList, selectWellDetailsSchematic, selectWellDetailsWell, selectIsGenerateFromSchematic],
  (groups, schematic, wellDetails, isGenerateFromSchematic) => {
    const schematicWithDefault = addDefaultSchematics(schematic, wellDetails);
    let copiedGroups = groups.slice();
    schematicWithDefault?.forEach((schematicItem, idx) => {
      const operationHeader = `${schematicItem.name || ''}${schematicItem.name ? ' ' : ''}${schematicItem.type}`;
      const headersList = groups.map((group) => group[0]?.operationHeader);

      if (!headersList?.includes(operationHeader)) {
        const updatedList = copiedGroups.slice();
        updatedList.splice(idx, 0, [
          {
            operationHeader,
            operationHeaderPosition: idx + 1,
          },
        ]);
        copiedGroups = updatedList;
      }
    });
    return isGenerateFromSchematic ? mapAndSortGroups(copiedGroups) : mapAndSortGroups(groups);
  },
);

export const selectActiveSectionId = createSelector(
  [selectActivityModel],
  (activityModel) => activityModel?.activeSection?.sectionId,
);

export const selectActiveSection = createSelector(
  [selectActivityModel],
  (activityModel) => activityModel?.activeSection,
);

export const selectActiveProjectSectionId = createSelector(
  [selectActivityModel],
  (activityModel) => activityModel.activeSection?.projectSectionId,
);

export const selectOperationListByActiveSection = createSelector(
  [selectOperationList, selectActiveProjectSectionId],
  (operationList, activeProjectSectionId) => {
    return operationList.filter((operation) => operation.projectSectionId === activeProjectSectionId);
  },
);

export const selectCodes = createSelector([selectActivityModel], (activityModel) => activityModel.codes);

export const selectSubOperationCodes = createSelector([selectCodes, selectActiveOperation], (codes, operation) => {
  const activeSubOperationCodes = codes.filter(
    (code) => code.type === operationCodesTypes.SUB_OPERATION && code.parentId === operation?.operationCode,
  );
  return activeSubOperationCodes.map(({ codeId, code, description }) => ({
    value: codeId,
    label: code,
    details: description,
  }));
});

export const isActivityWithSubs = createSelector(
  [selectActivityModel, (entities, operationId, taskId) => ({ operationId, taskId })],
  (activityModel, { operationId, taskId }) => {
    let result = false;
    const operation = activityModel.operations.find((operation) => operation.operationId === operationId);
    if (operation) {
      result = operation.tasks.find((task) => task.parentId === taskId && !task.isBranch);
    }

    return result;
  },
);

export const getRiskBranches = createSelector(
  [selectActivityModel, (entities, operationId, taskId) => ({ operationId, taskId })],
  (activityModel, { operationId, taskId }) => {
    let result = [];
    const operation = activityModel.operations.find((operation) => operation.operationId === operationId);
    if (operation) {
      result = operation.tasks.filter((task) => task.parentId === taskId && task.isBranch);
    }

    return result;
  },
);

export const selectSectionItems = createSelector([selectActivityModel], (activityModel) => activityModel.sectionItems);

export const selectSectionTypes = createSelector([selectActivityModel], (activityModel) => activityModel.sectionTypes);

const createLinkField = (label, value = null) => ({
  label,
  value: withUnit(value, 'm'),
  valueKey: label,
});

const findSchematicItemIndex = (activeSection, projectSections, schematic) => {
  const activeProjectSection = projectSections.find((ps) => ps.projectSectionId === activeSection?.projectSectionId);
  if (activeProjectSection) {
    const sameSectionItems = [...projectSections]
      .filter(
        (ps) =>
          ps.sectionId === activeProjectSection?.sectionId && ps.sectionTypeId === activeProjectSection?.sectionTypeId,
      )
      .sort((a, b) => a.position - b.position);

    const order = sameSectionItems.findIndex((ps) => ps.projectSectionId === activeProjectSection?.projectSectionId);

    const matchingSchematicItems = schematic.filter(
      (item) =>
        item.sectionId === activeProjectSection?.sectionId &&
        item.sectionTypeId === activeProjectSection?.sectionTypeId,
    );

    return schematic.indexOf(matchingSchematicItems[order]);
  }
  return -1;
};

export const selectExtendedLinkFields = createSelector(
  [
    selectLinkFields,
    selectWellDetailsSchematic,
    selectActiveSection,
    selectSectionsDataSections,
    selectSectionItems,
    selectSectionTypes,
  ],
  (linkFields, schematic, activeSection, sections, projectSections, sectionTypes) => {
    const reservoirSectionId = sections.find(({ name }) => name === Sections.Reservoir)?.sectionId;

    const pilotHoleSectionId = sections.find(({ name }) => name === Sections.PilotHole)?.sectionId;

    const tiebackTypeId = sectionTypes.find(({ name }) => name === casingStringType.TIEBACK)?.sectionTypeId;

    const sitePreparationSectionId = sections.find(({ name }) => name === Sections.SitePreparation)?.sectionId;

    const rigMoveSectionId = sections.find(({ name }) => name === Sections.RigMove)?.sectionId;

    const preSpudPreparationSectionId = sections.find(({ name }) => name === Sections.PreSpudPreparation)?.sectionId;

    const xmtSectionId = sections.find(({ name }) => name === Sections.XMT)?.sectionId;

    const lowerCompletionSectionId = sections.find(({ name }) => name === Sections.LowerCompletion)?.sectionId;

    const middleCompletionSectionId = sections.find(({ name }) => name === Sections.MiddleCompletion)?.sectionId;

    const upperCompletionSectionId = sections.find(({ name }) => name === Sections.UpperCompletion)?.sectionId;

    const activeSectionId = activeSection?.sectionId;

    const schematicItemIndex = findSchematicItemIndex(activeSection, projectSections, schematic);

    const activeItem = schematic[schematicItemIndex];

    const skipSectionIds = [
      lowerCompletionSectionId,
      middleCompletionSectionId,
      upperCompletionSectionId,
      sitePreparationSectionId,
      xmtSectionId,
      rigMoveSectionId,
      preSpudPreparationSectionId,
    ];

    const shouldSkipItem = (item) =>
      item.sectionTypeId === tiebackTypeId ||
      item.sectionId === pilotHoleSectionId ||
      item.sectionId === reservoirSectionId ||
      skipSectionIds.includes(item?.sectionId);

    const nonReservoirSkipSectionIds = skipSectionIds.filter((id) => id !== reservoirSectionId);

    const shouldSkipItemExceptReservoir = (item) =>
      item.sectionTypeId === tiebackTypeId ||
      item.sectionId === pilotHoleSectionId ||
      nonReservoirSkipSectionIds.includes(item?.sectionId);

    const getPreviousValidItem = (endIndex) => schematic.slice(0, endIndex).findLast((item) => !shouldSkipItem(item));

    const values = {
      [linkFieldsEnum.PREVIOUS_CASING_SHOE]: undefined,
      [linkFieldsEnum.SECTION_TD]: undefined,
      [linkFieldsEnum.CASING_SHOE]: undefined,
      [linkFieldsEnum.PREVIOUS_SECTION_TD]: undefined,
    };

    const isReservoirRelatedSection = [
      reservoirSectionId,
      lowerCompletionSectionId,
      middleCompletionSectionId,
      upperCompletionSectionId,
      xmtSectionId,
    ].includes(activeSectionId);

    const isTieback = activeItem?.sectionTypeId === tiebackTypeId;
    const isPilotHole = activeItem?.sectionId === pilotHoleSectionId;
    const isSitePreparation = activeSectionId === sitePreparationSectionId;
    const isRigMove = activeSectionId === rigMoveSectionId;
    const isPreSpudPreparation = activeSectionId === preSpudPreparationSectionId;
    const isXMT = activeSectionId === xmtSectionId;

    const isAdditionalSection =
      activeSectionId === sitePreparationSectionId ||
      activeSectionId === rigMoveSectionId ||
      activeSectionId === preSpudPreparationSectionId ||
      activeSectionId === xmtSectionId;

    if (isReservoirRelatedSection) {
      const reservoirIndex = schematic.findIndex(({ sectionId }) => sectionId === reservoirSectionId);

      const reservoirItem = schematic[reservoirIndex];
      const previousItem = getPreviousValidItem(reservoirIndex);

      values[linkFieldsEnum.PREVIOUS_CASING_SHOE] = previousItem?.bottom ?? activeItem?.bottom;
      values[linkFieldsEnum.SECTION_TD] = reservoirItem?.sectiontd;
      values[linkFieldsEnum.CASING_SHOE] = reservoirItem?.bottom;
      values[linkFieldsEnum.PREVIOUS_SECTION_TD] = previousItem?.sectiontd ?? 0;
    } else if (isTieback || isPilotHole) {
      const relatedItemIndex = schematic.slice(0, schematicItemIndex).findLastIndex((item) => !shouldSkipItem(item));

      const previousRelatedItemIndex = schematic
        .slice(0, relatedItemIndex)
        .findLastIndex((item) => !shouldSkipItem(item));

      const relatedItem = schematic[relatedItemIndex];
      const previousRelatedItem = schematic[previousRelatedItemIndex];

      values[linkFieldsEnum.PREVIOUS_CASING_SHOE] = previousRelatedItem?.bottom;
      values[linkFieldsEnum.SECTION_TD] = relatedItem?.sectiontd;
      values[linkFieldsEnum.CASING_SHOE] = relatedItem?.bottom;
      values[linkFieldsEnum.PREVIOUS_SECTION_TD] = previousRelatedItem?.sectiontd ?? 0;
    } else if (isAdditionalSection) {
      const lastValidIndex = schematic.findLastIndex((item) => !shouldSkipItemExceptReservoir(item));
      const lastValidItem = schematic[lastValidIndex];
      const previousItem = getPreviousValidItem(lastValidIndex);
      values[linkFieldsEnum.PREVIOUS_CASING_SHOE] = previousItem?.bottom;
      values[linkFieldsEnum.SECTION_TD] = lastValidItem?.sectiontd;
      values[linkFieldsEnum.CASING_SHOE] = lastValidItem?.bottom;
      values[linkFieldsEnum.PREVIOUS_SECTION_TD] = previousItem?.sectiontd;
    } else {
      const previousItem = getPreviousValidItem(schematicItemIndex);
      values[linkFieldsEnum.PREVIOUS_CASING_SHOE] = previousItem?.bottom;
      values[linkFieldsEnum.SECTION_TD] = activeItem?.sectiontd;
      values[linkFieldsEnum.CASING_SHOE] = activeItem?.bottom;
      values[linkFieldsEnum.PREVIOUS_SECTION_TD] = previousItem?.sectiontd;
    }

    return [
      ...linkFields,
      createLinkField(linkFieldsEnum.PREVIOUS_CASING_SHOE, values[linkFieldsEnum.PREVIOUS_CASING_SHOE]),
      createLinkField(linkFieldsEnum.SECTION_TD, values[linkFieldsEnum.SECTION_TD]),
      createLinkField(linkFieldsEnum.CASING_SHOE, values[linkFieldsEnum.CASING_SHOE]),
      createLinkField(linkFieldsEnum.PREVIOUS_SECTION_TD, values[linkFieldsEnum.PREVIOUS_SECTION_TD]),
    ].filter((item) => item.value !== null && item.value !== undefined && split(item.value)[0] !== '');
  },
);

export const selectSectionOperations = createSelector(
  [selectSectionItems, selectOperationList],
  (sections, operationList) => {
    return sections.reduce((acc, { projectSectionId, name }) => {
      const sectionOperations = operationList
        .filter((operation) => projectSectionId === operation.projectSectionId)
        .map(({ name, operationId }) => ({ name, id: operationId }));
      if (sectionOperations.length > 0) {
        acc.push({ projectSectionId, name, items: sectionOperations });
      }
      return acc;
    }, []);
  },
);

export const selectOpenTaskIds = createSelector([selectActivityModel], (activityModel) => activityModel.openTaskIds);
